import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  DeleteOutline as DeleteIcon,
  Description as PdfIcon,
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import { ConfirmDialog } from "@/shared/ui/ConfirmDialog";
import { formatBytes } from "@/shared/utils/formatBytes";
import {
  validatePolicyDocFile,
  type PolicyDoc,
  type PolicyDocDownloadUrl,
} from "@/features/stationAdmin/api/iaComplianceApi";
import {
  iaErrorMessage,
  type PolicyDocUploadState,
} from "@/features/stationAdmin/hooks/useIaPolicyDocs";
import { formatShortDate } from "./complianceView";

interface PolicyDocsCardProps {
  docs: PolicyDoc[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  uploadState: PolicyDocUploadState;
  onUpload: (file: File) => void;
  onClearUploadError: () => void;
  /** Rejects with the API reason; the confirm dialog surfaces it inline. */
  onDelete: (docId: string) => Promise<void>;
  removingDocId: string | null;
  /** ONE-SHOT presigned-URL mint (each mint writes a CJIS audit row). */
  onMintDownloadUrl: (docId: string) => Promise<PolicyDocDownloadUrl>;
  /** Roster name resolution for the uploaded-by line; unresolved ids render
   *  no uploader rather than a raw WorkOS id. */
  resolveUploaderName?: (userId: string) => string | undefined;
}

/**
 * Policy-document management: a single-file PDF dropzone (dropzone pattern
 * from UploadFilesToIncidentDialog), the station's available policy PDFs, and
 * per-row view/delete actions. Errors render inline (dialog/inline
 * convention — toasts are for page-level ops).
 */
export const PolicyDocsCard: React.FC<PolicyDocsCardProps> = ({
  docs,
  isLoading,
  isError,
  onRetry,
  uploadState,
  onUpload,
  onClearUploadError,
  onDelete,
  removingDocId,
  onMintDownloadUrl,
  resolveUploaderName,
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [openingDocId, setOpeningDocId] = useState<string | null>(null);
  const [mintedOpen, setMintedOpen] = useState<{
    fileName: string;
    url: string;
    expiresInMinutes: number;
  } | null>(null);
  const [confirmDoc, setConfirmDoc] = useState<PolicyDoc | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const uploading = uploadState.status === "uploading";

  const handlePicked = (picked: FileList | null) => {
    if (!picked || picked.length === 0 || uploading) return;
    if (picked.length > 1) {
      // Never silently upload an arbitrary one of several dropped files.
      setPickError("Upload one PDF at a time.");
      return;
    }
    const file = picked[0];
    const validationError = validatePolicyDocFile(file);
    if (validationError) {
      setPickError(validationError);
      return;
    }
    setPickError(null);
    onClearUploadError();
    onUpload(file);
  };

  const handleOpen = async (doc: PolicyDoc) => {
    if (openingDocId) return;
    setOpenError(null);
    setMintedOpen(null);
    setOpeningDocId(doc.docId);
    try {
      // One mint per intentional open — no re-mint timers (CJIS audit rows).
      const { downloadUrl, expiresIn } = await onMintDownloadUrl(doc.docId);
      // The click happens after an await, outside the user gesture's task, so
      // Safari/Firefox may popup-block it AFTER the audit row was written.
      // Keep the SAME minted URL available as a real anchor (a direct click
      // is never blocked) instead of re-minting — one open, one audit row.
      setMintedOpen({
        fileName: doc.fileName,
        url: downloadUrl,
        expiresInMinutes: Math.round(expiresIn / 60),
      });
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      setOpenError(
        iaErrorMessage(err, `Couldn't open "${doc.fileName}". Please retry.`),
      );
    } finally {
      setOpeningDocId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDoc) return;
    setDeleteError(null);
    try {
      await onDelete(confirmDoc.docId);
      setConfirmDoc(null);
    } catch (err) {
      setDeleteError(
        iaErrorMessage(err, "Couldn't delete the document. Please retry."),
      );
    }
  };

  const deleting = removingDocId !== null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700}>
          Policy Documents
        </Typography>
        <Typography variant="caption" color="text.secondary">
          IA and compliance policies (PDF, up to 20MB) that reports are checked
          against
        </Typography>

        <Box
          role="button"
          tabIndex={uploading ? -1 : 0}
          aria-disabled={uploading}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (uploading) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!uploading) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            // Always preventDefault, even mid-upload — otherwise the browser
            // navigates to the dropped file and the page is gone.
            e.preventDefault();
            setDragActive(false);
            if (!uploading) handlePicked(e.dataTransfer.files);
          }}
          sx={{
            mt: 2,
            border: `2px dashed ${
              dragActive
                ? theme.palette.primary.main
                : alpha(theme.palette.divider, 0.5)
            }`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            bgcolor: dragActive
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.action.hover, 0.3),
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
            transition: "border-color 0.2s, background-color 0.2s",
            "&:hover": uploading
              ? {}
              : {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => {
              handlePicked(e.target.files);
              e.target.value = "";
            }}
          />
          <UploadIcon
            sx={{
              fontSize: 40,
              color: alpha(theme.palette.text.secondary, 0.4),
              mb: 0.5,
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {uploading
              ? "Uploading…"
              : "Drag and drop a policy PDF here, or click to select"}
          </Typography>
        </Box>

        {pickError && (
          <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
            {pickError}
          </Typography>
        )}

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" noWrap>
              {uploadState.fileName}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadState.progress}
              sx={{ mt: 0.5, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Uploading… {Math.round(uploadState.progress)}%
            </Typography>
          </Box>
        )}

        {uploadState.status === "failed" && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={onClearUploadError}>
            {uploadState.fileName ? `${uploadState.fileName}: ` : ""}
            {uploadState.error ?? "Upload failed. Please try again."}
          </Alert>
        )}

        {openError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setOpenError(null)}>
            {openError}
          </Alert>
        )}

        {mintedOpen && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            onClose={() => setMintedOpen(null)}
          >
            If &quot;{mintedOpen.fileName}&quot; didn&apos;t open,{" "}
            <a
              href={mintedOpen.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMintedOpen(null)}
            >
              open it here
            </a>{" "}
            (link expires in {mintedOpen.expiresInMinutes} minutes).
          </Alert>
        )}

        {isError ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            Failed to load policy documents.
          </Alert>
        ) : isLoading ? (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
          </Stack>
        ) : docs.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontStyle: "italic" }}
          >
            No policy documents yet. Upload your station&apos;s IA policies to
            run compliance checks.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 2 }}>
            {docs.map((doc) => {
              const uploaderName = resolveUploaderName?.(doc.uploadedBy);
              const meta = [
                formatBytes(doc.sizeBytes),
                formatShortDate(doc.createdAt),
                uploaderName,
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <Stack
                  key={doc.docId}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  }}
                >
                  <PdfIcon fontSize="small" color="action" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {doc.fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {meta}
                    </Typography>
                  </Box>
                  <Tooltip title="View (opens in a new tab)">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`View ${doc.fileName}`}
                        onClick={() => void handleOpen(doc)}
                        disabled={openingDocId !== null}
                      >
                        {openingDocId === doc.docId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <OpenIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${doc.fileName}`}
                        onClick={() => {
                          setDeleteError(null);
                          setConfirmDoc(doc);
                        }}
                        disabled={deleting}
                      >
                        {removingDocId === doc.docId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        )}
      </CardContent>

      <ConfirmDialog
        open={confirmDoc !== null}
        onClose={() => setConfirmDoc(null)}
        onConfirm={() => void handleConfirmDelete()}
        title="Delete policy document?"
        description={
          <>
            &quot;{confirmDoc?.fileName}&quot; will no longer be available for
            compliance checks. Past run results are kept.
          </>
        }
        tone="error"
        loading={deleting}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      >
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </ConfirmDialog>
    </Card>
  );
};
